import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface ImpersonationState {
  active: boolean;
  targetOrgId: string | null;
  targetBrandName: string | null;
  targetUserEmail: string | null;
  expiresAt: string | null;
  loading: boolean;
  error: string | null;
}

const IMPERSONATION_TOKEN_KEY = "impersonation";
const IMPERSONATION_META_KEY = "impersonation_meta";

const readMeta = (): Partial<ImpersonationState> => {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_META_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const tokenPresent = !!sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);
const meta = readMeta();

const initialState: ImpersonationState = {
  active: tokenPresent,
  targetOrgId: meta.targetOrgId ?? null,
  targetBrandName: meta.targetBrandName ?? null,
  targetUserEmail: meta.targetUserEmail ?? null,
  expiresAt: meta.expiresAt ?? null,
  loading: false,
  error: null,
};

const impersonationSlice = createSlice({
  name: "impersonation",
  initialState,
  reducers: {
    startImpersonation: (
      _state,
      _action: PayloadAction<{ targetOrgId: string; reason: string }>,
    ) => {
      // handled by saga
    },
    stopImpersonation: () => {
      // handled by saga
    },
    impersonationStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    impersonationSuccess: (
      state,
      action: PayloadAction<{
        targetOrgId: string;
        targetBrandName: string | null;
        targetUserEmail: string;
        expiresAt: string;
      }>,
    ) => {
      state.loading = false;
      state.active = true;
      state.targetOrgId = action.payload.targetOrgId;
      state.targetBrandName = action.payload.targetBrandName;
      state.targetUserEmail = action.payload.targetUserEmail;
      state.expiresAt = action.payload.expiresAt;
      state.error = null;
    },
    impersonationFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    impersonationCleared: (state) => {
      state.loading = false;
      state.active = false;
      state.targetOrgId = null;
      state.targetBrandName = null;
      state.targetUserEmail = null;
      state.expiresAt = null;
      state.error = null;
    },
  },
});

export const IMPERSONATION_KEYS = {
  token: IMPERSONATION_TOKEN_KEY,
  meta: IMPERSONATION_META_KEY,
};

export const {
  startImpersonation,
  stopImpersonation,
  impersonationStart,
  impersonationSuccess,
  impersonationFailure,
  impersonationCleared,
} = impersonationSlice.actions;
export default impersonationSlice.reducer;
